import React from 'react';
import { Modal, Card, Avatar, Typography, Tag, Space, Row, Col, Divider } from 'antd';
import { UserOutlined, EnvironmentOutlined, BankOutlined, TeamOutlined, ProjectOutlined } from '@ant-design/icons';
import { Ambassador } from '../../types';
import { arteliaColors } from '../../theme';

const { Title, Text } = Typography;

interface AmbassadorModalProps {
  ambassador: Ambassador | null;
  visible: boolean;
  onClose: () => void;
}

const AmbassadorModal: React.FC<AmbassadorModalProps> = ({
  ambassador,
  visible,
  onClose,
}) => {
  if (!ambassador) return null;

  const isBooster = ambassador.role === 'Booster';
  
  // Generate initials for avatar
  const initials = ambassador.fullName
    .split(' ')
    .map(name => name.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      destroyOnHidden
      styles={{
        body: { padding: 0 }
      }}
    >
      <Card
        style={{ 
          border: 'none',
          borderRadius: 0,
        }}
        styles={{ body: { padding: '32px' } }}
      >
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Avatar
            size={80}
            style={{
              backgroundColor: isBooster ? arteliaColors.greenDarker : arteliaColors.bluePrimary,
              fontWeight: 600,
              fontSize: '24px',
              marginBottom: '16px',
            }}
            icon={!initials ? <UserOutlined /> : undefined}
          >
            {initials}
          </Avatar>
          
          <Title level={2} style={{ margin: '0 0 8px 0', color: arteliaColors.bluePrimary }}>
            {ambassador.fullName}
          </Title>
          
          <Space size="middle">
            <Tag 
              color={isBooster ? undefined : 'blue'}
              style={{ 
                fontSize: '12px',
                padding: '4px 8px',
                ...(isBooster && {
                  backgroundColor: arteliaColors.greenFaded,
                  color: arteliaColors.greenDarker,
                  border: `1px solid ${arteliaColors.greenBright}`,
                })
              }}
            >
              {ambassador.role}
            </Tag>
            <Tag color="default" style={{ fontSize: '12px', padding: '4px 8px' }}>
              {ambassador.businessUnit}
            </Tag>
          </Space>
        </div>

        <Divider />

        {/* Details Section */}
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              
              {/* Location */}
              <div>
                <Space align="start" size="small">
                  <EnvironmentOutlined style={{ fontSize: '16px', color: arteliaColors.bluePrimary, marginTop: '2px' }} />
                  <div>
                    <Text strong style={{ display: 'block', fontSize: '14px', color: '#333' }}>
                      Location
                    </Text>
                    <Text style={{ fontSize: '13px', color: '#666' }}>
                      {ambassador.city}, {ambassador.country}
                    </Text>
                    {ambassador.region && (
                      <Text style={{ fontSize: '12px', color: '#999', display: 'block' }}>
                        Region: {ambassador.region}
                      </Text>
                    )}
                  </div>
                </Space>
              </div>

              {/* Business Unit */}
              <div>
                <Space align="start" size="small">
                  <BankOutlined style={{ fontSize: '16px', color: arteliaColors.bluePrimary, marginTop: '2px' }} />
                  <div>
                    <Text strong style={{ display: 'block', fontSize: '14px', color: '#333' }}>
                      Business Unit
                    </Text>
                    <Text style={{ fontSize: '13px', color: '#666' }}>
                      {ambassador.businessUnit}
                    </Text>
                    {ambassador.department && (
                      <Text style={{ fontSize: '12px', color: '#999', display: 'block' }}>
                        Department: {ambassador.department}
                      </Text>
                    )}
                  </div>
                </Space>
              </div>
            </Space>
          </Col>

          <Col span={12}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              
              {/* Role */}
              <div>
                <Space align="start" size="small">
                  <TeamOutlined style={{ fontSize: '16px', color: arteliaColors.bluePrimary, marginTop: '2px' }} />
                  <div>
                    <Text strong style={{ display: 'block', fontSize: '14px', color: '#333' }}>
                      Role & Responsibilities
                    </Text>
                    <Text style={{ fontSize: '13px', color: '#666' }}>
                      {ambassador.role}
                    </Text>
                    <Text style={{ fontSize: '12px', color: '#999', display: 'block', marginTop: '4px' }}>
                      {isBooster 
                        ? 'Leading sustainability initiatives and mentoring team members'
                        : 'Contributing to eco-design projects and sustainability practices'
                      }
                    </Text>
                  </div>
                </Space>
              </div>

              {/* Activity */}
              <div>
                <Space align="start" size="small">
                  <ProjectOutlined style={{ fontSize: '16px', color: arteliaColors.bluePrimary, marginTop: '2px' }} />
                  <div>
                    <Text strong style={{ display: 'block', fontSize: '14px', color: '#333' }}>
                      Primary Activity
                    </Text>
                    <Text style={{ fontSize: '13px', color: '#666' }}>
                      {ambassador.activity}
                    </Text>
                    <Text style={{ fontSize: '12px', color: '#999', display: 'block', marginTop: '4px' }}>
                      Specialized expertise and focus area
                    </Text>
                  </div>
                </Space>
              </div>
            </Space>
          </Col>
        </Row>

        <Divider />

        {/* Footer */}
        <div style={{ textAlign: 'center', color: '#999', fontSize: '12px' }}>
          <Text type="secondary">
            Part of the Artelia TRACE Ambassador Network • Eco-Design Program
          </Text>
        </div>
      </Card>
    </Modal>
  );
};

export default AmbassadorModal;